import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { ColorPicker } from "@/pages/ColorListPage";

const DEFAULT_ORG_COLOR = "#0d9488";

export default function OrganisationsPage() {
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState(DEFAULT_ORG_COLOR);

  const load = async () => {
    try {
      const { data } = await api.get("/organisations");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  if (user && user.role !== "admin") return <Navigate to="/timer" replace />;

  const openCreate = () => {
    setEditing(null);
    setName("");
    setColor(DEFAULT_ORG_COLOR);
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setName(it.name);
    setColor(it.color || DEFAULT_ORG_COLOR);
    setOpen(true);
  };

  const save = async () => {
    if (!name.trim()) return;
    try {
      if (editing) {
        await api.put(`/organisations/${editing.id}`, { name: name.trim(), color });
        toast.success("Organisation updated");
      } else {
        await api.post("/organisations", { name: name.trim(), color });
        toast.success(`Created "${name.trim()}" — you've been added as the first member`);
        await refresh(); // creator auto-added + active_org switched by backend
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"? Members in it will be moved to another organisation, and its clients/activities/time entries will be removed.`)) return;
    try {
      await api.delete(`/organisations/${it.id}`);
      load();
      refresh();
      toast.success("Organisation deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const switchTo = async (it) => {
    try {
      await api.post("/organisations/switch", { organisation_id: it.id });
      await refresh();
      toast.success(`Switched to ${it.name}`);
      window.location.reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const activeId = user?.active_organisation_id || user?.organisation_id;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-8">
      <div className="max-w-5xl mx-auto w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Manrope" }}>Organisations</h1>
          <p className="text-sm text-slate-500 mt-1">Top-level workspaces. Members, Clients, Activities and Time Entries are scoped to the active organisation.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="text-white" style={{ background: "#145c8c" }}
                    data-testid="org-add-button">
              <Plus className="h-4 w-4 mr-1" /> New Organisation
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Organisation" : "New Organisation"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Organisation name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1"
                       placeholder="e.g. Dreamtime Resorts"
                       data-testid="org-name-input" />
              </div>
              <div>
                <Label>Brand colour</Label>
                <div className="mt-1">
                  <ColorPicker value={color} onChange={setColor} testId="org-color-picker" />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  The sidebar tints to a light shade of this colour so you always know which workspace you&apos;re in.
                </p>
              </div>
              {!editing && (
                <p className="text-xs text-slate-500 border border-slate-200 rounded-md p-3 bg-slate-50">
                  You&apos;ll be added as the first team member and switched to this organisation automatically.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="text-white" style={{ background: "#145c8c" }}
                      data-testid="org-save-button">
                {editing ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {items.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-sm">No organisations yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 w-10"></th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100" data-testid={`org-row-${it.id}`}>
                  <td className="px-4 py-3">
                    <span
                      className="inline-block h-4 w-4 rounded-full border border-slate-200"
                      style={{ background: it.color || DEFAULT_ORG_COLOR }}
                      data-testid={`org-swatch-${it.id}`}
                    />
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{it.name}</td>
                  <td className="px-4 py-3">
                    {activeId === it.id ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#e8f1f8] text-[#0b2c47] font-medium">
                        <Check className="h-3 w-3" /> Active
                      </span>
                    ) : (
                      <button onClick={() => switchTo(it)}
                              className="text-xs text-[#145c8c] hover:underline"
                              data-testid={`org-switch-${it.id}`}>
                        Switch to this
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(it)}
                            className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500"
                            data-testid={`org-edit-${it.id}`}>
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(it)}
                            className="p-1.5 hover:bg-red-50 rounded-md text-red-500"
                            data-testid={`org-delete-${it.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </div>
    </div>
  );
}
